/**
 * PATH: apps/quote/src/components/builder/unified-bottom-bar/UnifiedBottomBar.tsx
 * PURPOSE: Orchestrator for unified bottom bar (actions + sheet switch + status)
 */

import { Plus, Layers, ClipboardPaste, FileUp, Calculator, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspace } from '../BuilderWorkspace'
import type { QuoteSection } from '@/lib/quoteApi'
import { SheetSwitcher } from './SheetSwitcher'
import { PanelToggles } from './PanelToggles'
import { SaveStatusIndicator, CompactTotals, KeyboardHintsRow } from './StatusWidgets'

function CommandButton({ icon: Icon, label, shortcut, onClick, disabled, variant = 'default', className }: {
  icon: React.ComponentType<{ className?: string }>; label: string; shortcut?: string
  onClick: () => void; disabled?: boolean; variant?: 'default' | 'primary' | 'success'; className?: string
}) {
  return (
    <button onClick={onClick} disabled={disabled} className={cn(
      'flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
      variant === 'default' && 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
      variant === 'primary' && 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30',
      variant === 'success' && 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
      className
    )} title={shortcut ? `${label} (${shortcut})` : label}>
      <Icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{label}</span>
    </button>
  )
}

interface UnifiedBottomBarProps {
  sections: QuoteSection[]; isEditable: boolean; onAddSection: () => void; onAddRow: () => void
  onPaste: () => void; onImportExcel: () => void; onRecalculate: () => void
  isRecalculating?: boolean; isAddingSectionPending?: boolean
  totals?: { totalEx: number; marginPercent: number; manDays: number }
}

export default function UnifiedBottomBar({ sections, isEditable, onAddSection, onAddRow, onPaste, onImportExcel, onRecalculate, isRecalculating, isAddingSectionPending, totals }: UnifiedBottomBarProps) {
  const { selection } = useWorkspace()

  return (
    <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
      <KeyboardHintsRow />
      <div className="flex items-center h-9 px-2">
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <CommandButton icon={Layers} label="Section" shortcut="Alt+S" onClick={onAddSection} disabled={!isEditable} />
          <CommandButton icon={Plus} label="Row" shortcut="Alt+R" onClick={onAddRow} disabled={!isEditable || !selection.sectionId} />
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
          <CommandButton icon={ClipboardPaste} label="Paste" shortcut="Ctrl+V" onClick={onPaste} disabled={!isEditable} />
          <CommandButton icon={FileUp} label="Import" onClick={onImportExcel} disabled={!isEditable} />
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
          <CommandButton icon={isRecalculating ? Loader2 : Calculator} label="Recalc" onClick={onRecalculate} disabled={isRecalculating} variant="success" />
        </div>
        <SheetSwitcher sections={sections} onAddSection={onAddSection} isAddingSectionPending={isAddingSectionPending} />
        <div className="flex items-center gap-2 flex-shrink-0">
          <CompactTotals totals={totals} />
          <SaveStatusIndicator />
          <PanelToggles />
        </div>
      </div>
    </div>
  )
}
